/**
 * Reputation copy-generation contracts.
 *
 * A leaf module on purpose: `jobs.ts` stores these on job metadata and
 * `automation-template.ts` re-exports them, and those two already form a cycle
 * through `business-automation.ts`.
 */
import { z } from 'zod';
/**
 * Image styles offered when a partner opts into generated imagery. Mirrors the six
 * the single-email generator already offers (AiEmailBuilderImageStyle).
 */
export const reputationImageStyleSchema = z.enum([
    'illustration',
    'photorealistic',
    'graphic_design',
    'cartoon',
    'minimalist',
    'watercolor',
]);
/**
 * How one message of one template ended up. `unchanged` is a message the model
 * produced nothing usable for; it keeps the copy it already ships, as does `failed`.
 */
export const reputationMessageGenerationStatusSchema = z.enum([
    'pending',
    'generated',
    'unchanged',
    'failed',
]);
export const reputationTemplateGenerationStatusSchema = z.enum([
    'pending',
    'generating',
    'completed',
    'failed',
]);
export var ReputationGenerationRunStatus;
(function (ReputationGenerationRunStatus) {
    ReputationGenerationRunStatus["PROCESSING"] = "PROCESSING";
    ReputationGenerationRunStatus["COMPLETED"] = "COMPLETED";
    ReputationGenerationRunStatus["FAILED"] = "FAILED";
})(ReputationGenerationRunStatus || (ReputationGenerationRunStatus = {}));
export const reputationGeneratedMessageSchema = z.object({
    /** Index of the send action within the template, unique per template. */
    actionIndex: z.number(),
    /** What this message is for, e.g. `google-review-reminder`. */
    reputationTemplateType: z.string().optional(),
    status: reputationMessageGenerationStatusSchema,
    /** Why this message kept its shipped copy. */
    error: z.string().optional(),
    /** Why this message kept its shipped illustration, when the copy itself was written. */
    imageError: z.string().optional(),
});
export const reputationGeneratedTemplateSchema = z.object({
    templateId: z.string(),
    templateName: z.string(),
    displayName: z.string(),
    status: reputationTemplateGenerationStatusSchema,
    error: z.string().optional(),
    messages: z.array(reputationGeneratedMessageSchema),
});
export const reputationGenerationStatusResponseSchema = z.object({
    status: z.nativeEnum(ReputationGenerationRunStatus),
    /** Present when the run itself failed before or independently of any template. */
    error: z.string().optional(),
    templates: z.array(reputationGeneratedTemplateSchema),
});
//# sourceMappingURL=reputation-generation.js.map